import { Grid, Skeleton, Typography, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useDateTime } from "../../../../localization/useDateTime"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    fetchSupportPrinterQueueDetails,
    SupportPrinterQueueDetailsResponse,
    SupportReportsPrinterQueueQuery,
} from "../printerQueueApi"

const useStyles = makeStyles()(() => ({
    textGray: {
        color: tailwindPalette.gray[500],
    },
}))
function transformData(data: SupportPrinterQueueDetailsResponse) {
    return data?.printerQueueDetails || []
}

export function DetailedPanel({ id }: { id: number }) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { data, isLoading } = useApiQuery(fetchSupportPrinterQueueDetails, {
        queryName: SupportReportsPrinterQueueQuery.DETAILS_PRINTER_QUEUE_FOR_SUPPORT,
        dependencies: {
            printerQueueId: id,
        },
        select: transformData,
    })

    return (
        <Stack direction="column" spacing="1rem">
            {isLoading ? (
                <Stack maxWidth="20rem" direction="column" spacing="1rem">
                    <Skeleton variant="rectangular" animation="wave" />
                    <Skeleton variant="rectangular" animation="wave" />
                    <Skeleton variant="rectangular" animation="wave" />
                </Stack>
            ) : (
                <>
                    <Stack direction="column" spacing=".3rem">
                        <Grid container>
                            <Grid item xs={1.5}>
                                <LocalizedStrict id="support-printer-details-id-label">
                                    <Typography className={classes.textGray}>ID:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>{id}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={1.5}>
                                <LocalizedStrict id="support-printer-details-status-label">
                                    <Typography className={classes.textGray}>Status:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>{data?.status}</Typography>
                            </Grid>
                        </Grid>
                        {data?.created && (
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-created-label">
                                        <Typography className={classes.textGray}>Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>
                                        {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(data?.created))}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                        {data?.printAt && (
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-printed-at-label">
                                        <Typography className={classes.textGray}>Printed at:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>
                                        {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(data?.printAt))}
                                    </Typography>
                                </Grid>
                            </Grid>
                        )}
                    </Stack>

                    <Stack direction="column" spacing=".3rem">
                        <LocalizedStrict id="support-printer-details-device-group-subtitle">
                            <Typography className={classes.textGray}>Device group:</Typography>
                        </LocalizedStrict>
                        <Grid container>
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-id-label">
                                        <Typography className={classes.textGray}>ID:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>{data?.deviceGroup.id}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-name-label">
                                        <Typography className={classes.textGray}>Name:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>{data?.deviceGroup.name}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Stack>

                    <Stack direction="column" spacing=".3rem">
                        <LocalizedStrict id="support-printer-details-device-subtitle">
                            <Typography className={classes.textGray}>Device:</Typography>
                        </LocalizedStrict>
                        <Grid container>
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-id-label">
                                        <Typography className={classes.textGray}>ID:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>{data?.device.id}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={1.5}>
                                    <LocalizedStrict id="support-printer-details-name-label">
                                        <Typography className={classes.textGray}>Name:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs="auto">
                                    <Typography>{data?.device.name}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Stack>

                    <Stack direction="column" spacing=".3rem">
                        <Grid container>
                            <Grid item xs={1.5}>
                                <LocalizedStrict id="support-printer-details-mime-type-label">
                                    <Typography className={classes.textGray}>Mime type:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>{data?.mimeType}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={1.5}>
                                <LocalizedStrict id="support-printer-details-priority-label">
                                    <Typography className={classes.textGray}>Priority:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>{data?.priority}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={1.5}>
                                <LocalizedStrict id="support-printer-details-job-token-label">
                                    <Typography className={classes.textGray}>Job token:</Typography>
                                </LocalizedStrict>
                            </Grid>
                            <Grid item xs="auto">
                                <Typography>{data?.job_token}</Typography>
                            </Grid>
                        </Grid>
                    </Stack>
                </>
            )}
        </Stack>
    )
}
